import { getCorrelationId, CORRELATION_HEADER } from 'src/lib/correlation'
import { logger } from 'src/lib/logger'
import { includes, pageTypes } from 'src/lib/react-query/fields'
import {
  ApiError,
  FetchError,
  FetchOptions,
  FetchResponse,
  PageFetch,
  PageRouterPath
} from 'src/lib/react-query/types'

import type { AdFormatsCollectionResponse } from 'src/lib/react-query/adFormatsList'
import type { MenuApiResponse, PageTypeResponse } from 'src/types/json-api'

const isServer = typeof window === 'undefined'
export const API_URL = process.env.API_URL || 'http://drupal:80'

const API_TIMEOUT_MS = Number(process.env.API_TIMEOUT_MS ?? 3000)
const HEADER_CACHE_TTL_MS = 60_000

let headerCache: { expiresAt: number; value: FetchResponse<MenuApiResponse> } | null = null
let headerInflight: Promise<FetchResponse<MenuApiResponse>> | null = null

const getFetchError = (error: any): FetchError => {
  const apiError = error as ApiError

  return {
    type: apiError.name,
    statusText: apiError.error
  }
}

const catchErrorCodes = (response: Response): void => {
  if (response.ok) return

  const errors: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout'
  }

  const error = (response && errors[response.status]) || errors[500]

  throw {
    name: error && response ? 'Api Error' : 'Generic Error',
    error,
    response
  }
}

const logPageFetchIssue = (path: string, err: unknown): FetchError => {
  const error = getFetchError(err)
  const meta = { path, error: err }

  if (error.statusText === 'Not Found') {
    logger.warn('Page fetch error', meta)
  } else {
    logger.error('Page fetch error', meta)
  }

  return error
}

export async function apiFetch<T>(url: string, options?: FetchOptions): Promise<T> {
  const fetchOptions = options || {}

  const correlationId = getCorrelationId()
  const headers = new Headers(fetchOptions.headers)
  if (correlationId) {
    headers.set(CORRELATION_HEADER, correlationId)
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: fetchOptions.signal ?? AbortSignal.timeout(API_TIMEOUT_MS)
    })

    catchErrorCodes(response)

    return response.json()
  } catch (err) {
    return Promise.reject(err)
  }
}

async function fetchHeaderDataUncached(): Promise<FetchResponse<MenuApiResponse>> {
  try {
    const menuItems = await apiFetch<MenuApiResponse>(
      isServer ? `${API_URL}/jsonapi/menu_items/main` : '/api/header'
    )
    return { data: menuItems, error: null, isError: false }
  } catch (err) {
    const error = getFetchError(err)
    logger.error('Header Data Fetch Error', { path: 'Header Data', error: err })

    return { data: null, error, isError: true }
  }
}

export async function fetchHeaderData(): Promise<FetchResponse<MenuApiResponse>> {
  const now = Date.now()
  if (headerCache && headerCache.expiresAt > now) {
    return headerCache.value
  }

  if (!headerInflight) {
    headerInflight = fetchHeaderDataUncached().finally(() => {
      headerInflight = null
    })
  }

  const result = await headerInflight

  if (!result.isError && result.data) {
    headerCache = {
      expiresAt: now + HEADER_CACHE_TTL_MS,
      value: result
    }
  }

  return result
}

export async function fetchPageData<T extends PageFetch>(path: string): Promise<FetchResponse<T>> {
  let uuid = ''
  try {
    await fetchRouterTranslatePath(`/${path}`).then(p => (uuid = p.uuid))

    const route = `${uuid}${getPageIncludes(path.split('/')[0])}`
    const res = await apiFetch<T>(`${API_URL}/jsonapi/node/standard_page/${route}`)
    return { data: res, error: null, isError: false }
  } catch (err) {
    const error = logPageFetchIssue(uuid, err)

    return { data: null, error, isError: true }
  }
}

export async function fetchAdFormatsList(): Promise<FetchResponse<AdFormatsCollectionResponse>> {
  try {
    const include = includes.adFormatsList.join(',')
    const res = await apiFetch<AdFormatsCollectionResponse>(
      `${API_URL}/jsonapi/node/${pageTypes.adFormats}?page%5Blimit%5D=50&include=${include}`
    )

    return {
      data: res,
      error: null,
      isError: false
    }
  } catch (err) {
    const error = getFetchError(err)
    logger.error('Ad Formats List Fetch Error', { path: pageTypes.adFormats, error: err })

    return { data: null, error, isError: true }
  }
}

export async function fetchPageType(type: string): Promise<FetchResponse<PageFetch[]>> {
  try {
    const res = await apiFetch<PageTypeResponse>(`${API_URL}/jsonapi/node/${type}`)

    return {
      data: res.data,
      error: null,
      isError: false
    }
  } catch (err) {
    const error = getFetchError(err)
    logger.error('Page Type Fetch Error', { path: type, error: err })

    return { data: null, error, isError: true }
  }
}

export async function fetchRouterTranslatePath(path: string): Promise<{ uuid: string }> {
  try {
    const res = await apiFetch<PageRouterPath | null>(
      `${API_URL}/router/translate-path?path=${encodeURIComponent(path)}`
    )

    return { uuid: (res && res.entity && res.entity.uuid) || '' }
  } catch (err) {
    logPageFetchIssue(path, err)

    return Promise.reject(err)
  }
}

export function getPageIncludes(segment: string): string {
  return (
    '?include=' +
      {
        home: includes.home,
        platforms: includes.standard,
        advertising: includes.standard,
        audiences: includes.standard,
        adformats: includes.adformats
      }[segment.replaceAll('-', '').toLocaleLowerCase()] || segment
  )
}
